import React from "react";
import { Helmet } from "react-helmet";
import Header from "../Header";
import Footer from "../Footer";
import "./BusinessWebsites.css";

export default function BusinessWebsites() {
    const services = [
        {
            image: "assets/img/GameDevelopment/web-development-compnay-in-california.jpg",
            title: "Corporate Websites",
            description: "Professional company websites with strong messaging, service pages, inquiry flows, responsive layouts, and SEO-ready structure.",
            points: ["Company profile", "Service pages", "Lead forms"]
        },
        {
            image: "assets/img/GameDevelopment/react/website-builder.png",
            title: "Service Business Websites",
            description: "Conversion-focused websites for agencies, IT companies, consultants, studios, and local service businesses.",
            points: ["Offer pages", "Trust sections", "Contact CTAs"]
        },
        {
            image: "assets/img/service/HTML_serv_1.jpg",
            title: "Product Landing Pages",
            description: "Fast landing pages for SaaS products, mobile apps, games, campaigns, MVP launches, and investor-ready presentations.",
            points: ["Hero content", "Feature blocks", "CTA strategy"]
        },
        {
            image: "assets/img/GameDevelopment/react/gallery_1_8.jpg",
            title: "Portfolio & Case Study Sites",
            description: "Showcase your work, case studies, testimonials, expertise, and outcomes with clean navigation and polished content hierarchy.",
            points: ["Case studies", "Work gallery", "Client proof"]
        }
    ];

    const websiteTypes = [
        { image: "assets/img/service/HTML_serv_2.jpg", title: "Startup Website", description: "Clear product story, feature sections, lead capture, and SEO foundation for early-stage businesses." },
        { image: "assets/img/service/HTML_serv_3.jpg", title: "Agency Website", description: "Service categories, portfolio, team, testimonials, process, and inquiry forms for business growth." },
        { image: "assets/img/service/HTML_serv_4.jpg", title: "IT Company Website", description: "Technology pages, delivery model, service expertise, case studies, and consultation-focused CTAs." },
        { image: "assets/img/service/HTML_serv_5.jpg", title: "Game Studio Website", description: "Game portfolio, store links, development services, studio profile, careers, and project inquiry flow." },
        { image: "assets/img/GameDevelopment/react/SaaS-1.png", title: "SaaS Marketing Website", description: "Feature storytelling, pricing sections, integrations, FAQs, demos, and conversion-focused navigation." },
        { image: "assets/img/GameDevelopment/Landing-Page-Design-1.png", title: "Campaign Landing Page", description: "Focused single-page experience for ads, product launches, app downloads, and lead-generation campaigns." }
    ];

    const deliverables = [
        "SEO Page Structure",
        "Responsive UI",
        "Homepage Design",
        "Service Pages",
        "About Page",
        "Contact Flow",
        "Case Study Layout",
        "Speed Optimization",
        "Analytics Setup",
        "CMS Ready Structure",
        "Conversion CTAs",
        "Deployment Support"
    ];

    const gallery = [
        { image: "assets/img/GameDevelopment/react/home-banner.jpg", title: "Homepage Hero" },
        { image: "assets/img/GameDevelopment/react/hero-banner-1.png", title: "SaaS Website" },
        { image: "assets/img/GameDevelopment/react/gallery_1_1.jpg", title: "Agency Layout" },
        { image: "assets/img/GameDevelopment/react/solution-place.png", title: "Product Section" },
        { image: "assets/img/GameDevelopment/react/process-img.png", title: "Process Section" },
        { image: "assets/img/GameDevelopment/react/yugu-1024x675.png", title: "Case Study View" },
        { image: "assets/img/GameDevelopment/react/efoivn-1024x541.png", title: "Dashboard Preview" },
        { image: "assets/img/GameDevelopment/react/rgcedb-1024x698.png", title: "Portfolio Detail" }
    ];

    const process = [
        { step: "01", title: "Discovery & SEO Plan", description: "We define business goals, audience, keywords, sitemap, page priorities, and conversion actions." },
        { step: "02", title: "Content Structure", description: "We organize hero copy, service content, trust sections, proof points, FAQs, and call-to-action placement." },
        { step: "03", title: "UI Design", description: "We design responsive layouts with clear hierarchy, readable typography, strong visuals, and brand consistency." },
        { step: "04", title: "Development", description: "We build clean, responsive pages with optimized assets, reusable sections, forms, analytics, and SEO tags." },
        { step: "05", title: "Launch & Improve", description: "We test performance, responsiveness, forms, metadata, redirects, analytics, and prepare the website for launch." }
    ];

    const benefits = [
        { icon: "fa-solid fa-magnifying-glass-chart", title: "SEO-Ready Pages", description: "Page titles, meta descriptions, headings, internal structure, and content blocks are planned for search visibility." },
        { icon: "fa-solid fa-mobile-screen-button", title: "Responsive Experience", description: "Your site works cleanly across desktop, laptop, tablet, and mobile screens with stable layouts." },
        { icon: "fa-solid fa-bolt", title: "Fast Loading", description: "We focus on optimized visuals, clean sections, and practical performance improvements for better user experience." },
        { icon: "fa-solid fa-handshake", title: "Lead-Focused Design", description: "Content, trust signals, contact buttons, and forms are arranged to help visitors take the next step." }
    ];

    const priorities = [
        { label: "Positioning", value: "Clear offer, audience, and proof above the fold" },
        { label: "Navigation", value: "Simple paths to services, work, company, and contact" },
        { label: "Conversion", value: "CTA placement, enquiry flow, trust signals, and forms" },
        { label: "Search", value: "SEO hierarchy, metadata, content depth, and internal links" }
    ];

    const architecture = [
        { step: "01", title: "Buyer Journey", description: "Homepage, service pages, proof, FAQs, and contact paths are arranged around how visitors decide." },
        { step: "02", title: "SEO Foundation", description: "Headings, page hierarchy, keyword sections, internal links, and schema are planned before design." },
        { step: "03", title: "Conversion System", description: "Trust blocks, CTAs, enquiry forms, and consultation paths are placed where intent is strongest." }
    ];

    const faqs = [
        {
            question: "What business website services do you provide?",
            answer: "We create corporate websites, service websites, product landing pages, portfolio websites, SaaS marketing websites, and game studio websites."
        },
        {
            question: "Will the website be SEO optimized?",
            answer: "Yes. We plan SEO-friendly page structure, headings, metadata, content sections, internal links, image alt text, and fast responsive layouts."
        },
        {
            question: "Can you write website content?",
            answer: "Yes. We can improve homepage, service page, about page, CTA, FAQ, and section copy so it is clear for users and search engines."
        },
        {
            question: "Can you build websites for game and IT companies?",
            answer: "Yes. Oengines builds websites for game studios, IT companies, SaaS products, mobile app businesses, agencies, and service brands."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Business Website Development Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Business Website Development",
        "areaServed": "Worldwide",
        "description": "SEO-ready business website development services for corporate websites, service websites, SaaS landing pages, game studio websites, portfolios, and lead-generation websites.",
        "offers": services.map((service) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": service.title,
                "description": service.description
            }
        }))
    };

    return (
        <>
            <Helmet>
                <title>Business Website Development Services | SEO Website Design | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds SEO-ready business websites, corporate websites, SaaS landing pages, game studio websites, portfolios, and lead-generation websites." />
                <meta name="keywords" content="business website development, corporate website design, SEO website development, business website design, landing page development, game studio website, IT company website" />
                <link rel="canonical" href="https://www.oengines.com/business-websites" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="business-web-page">
                <section className="business-web-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-xl-6 col-lg-7">
                                <span className="business-web-eyebrow">Business Website Development</span>
                                <h1>SEO-Ready Business Websites Built to Explain, Convert & Grow</h1>
                                <p>Oengines Games LLP designs and develops business websites for IT companies, game studios, startups, SaaS products, service brands, and agencies that need clear content, fast performance, and strong lead flow.</p>
                                <div className="business-web-actions">
                                    <a href="/contact" className="as-btn">Build My Website <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="business-web-call">Call: (0261) - 4054978</a>
                                </div>
                                <div className="business-web-hero-stats">
                                    <div><strong>SEO</strong><span>Ready Structure</span></div>
                                    <div><strong>Fast</strong><span>Responsive Pages</span></div>
                                    <div><strong>Lead</strong><span>Focused CTAs</span></div>
                                </div>
                            </div>
                            <div className="col-xl-6 col-lg-5">
                                <div className="business-web-browser-wrap">
                                    <div className="business-web-browser">
                                        <div className="business-web-browser-top">
                                            <span></span><span></span><span></span>
                                            <strong>oengines.com/business</strong>
                                        </div>
                                        <img src="assets/img/GameDevelopment/react/website-builder.png" alt="Business website preview" />
                                        <div className="business-web-browser-card">
                                            <span>Website Score</span>
                                            <strong>SEO + UX + Leads</strong>
                                        </div>
                                    </div>
                                    <div className="business-web-code-card">
                                        <span>Build Focus</span>
                                        <ul>
                                            <li>Fast responsive layout</li>
                                            <li>SEO page hierarchy</li>
                                            <li>Lead-generation flow</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="business-web-priority-section">
                    <div className="container">
                        <div className="business-web-priority-grid">
                            {priorities.map((item) => (
                                <article className="business-web-priority-card" key={item.label}>
                                    <span>{item.label}</span>
                                    <p>{item.value}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="business-web-service-section space">
                    <div className="container">
                        <div className="business-web-split-heading">
                            <div className="business-web-heading mb-0">
                                <span>Website Services</span>
                                <h2>Business Websites Designed for Trust, SEO & Conversions</h2>
                            </div>
                            <p>We build websites that clearly explain your services, show your proof, guide visitors, and make it easy to contact your team. Each section is planned around content clarity, user intent, and business enquiry flow.</p>
                        </div>
                        <div className="business-web-service-list">
                            {services.map((service) => (
                                <article className="business-web-card" key={service.title}>
                                    <img src={service.image} alt={service.title} />
                                    <div>
                                        <h3>{service.title}</h3>
                                        <p>{service.description}</p>
                                    </div>
                                    <ul>
                                        {service.points.map((point) => <li key={point}>{point}</li>)}
                                    </ul>
                                    <i className="fa-regular fa-arrow-right"></i>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="business-web-showcase-section space">
                    <div className="container">
                        <div className="row align-items-center g-5">
                            <div className="col-lg-5">
                                <div className="business-web-heading business-web-architecture-heading">
                                    <span>Website Architecture</span>
                                    <h2>Designed Like a Business System, Not Only a Visual Page</h2>
                                    <p>We organize your website around the pages and sections buyers expect: services, proof, process, outcomes, FAQs, contact paths, and industry-specific messaging.</p>
                                </div>
                                <div className="business-web-architecture-stack">
                                    {architecture.map((item) => (
                                        <article className="business-web-architecture-item" key={item.step}>
                                            <span>{item.step}</span>
                                            <div>
                                                <h3>{item.title}</h3>
                                                <p>{item.description}</p>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                                <div className="business-web-architecture-list">
                                    {deliverables.slice(0, 5).map((asset) => <span key={asset}>{asset}</span>)}
                                </div>
                            </div>
                            <div className="col-lg-7">
                                <div className="business-web-wireframe">
                                    <div className="business-web-wireframe-top">
                                        <span>Logo</span>
                                        <span>Services</span>
                                        <span>Work</span>
                                        <span>Contact</span>
                                    </div>
                                    <div className="business-web-wireframe-hero">
                                        <div>
                                            <span>Hero + CTA</span>
                                            <strong>Clear business value proposition</strong>
                                            <p>Who you help, what you build, and why visitors should contact you.</p>
                                        </div>
                                        <em>Primary CTA</em>
                                    </div>
                                    <div className="business-web-wireframe-path">
                                        <span>Problem</span>
                                        <i></i>
                                        <span>Services</span>
                                        <i></i>
                                        <span>Proof</span>
                                        <i></i>
                                        <span>Enquiry</span>
                                    </div>
                                    <div className="business-web-wireframe-grid">
                                        <div><strong>Service Pages</strong><small>SEO content blocks</small></div>
                                        <div><strong>Case Studies</strong><small>Proof and outcomes</small></div>
                                        <div><strong>Trust Signals</strong><small>Reviews, clients, process</small></div>
                                        <div><strong>Contact Flow</strong><small>Forms, call, consultation</small></div>
                                    </div>
                                    <div className="business-web-wireframe-bottom">
                                        <span>FAQ + Schema</span>
                                        <span>Analytics + Speed</span>
                                        <span>Launch Checklist</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="business-web-type-section space">
                    <div className="container">
                        <div className="business-web-heading text-center">
                            <span>Website Types</span>
                            <h2>Business Website Solutions We Can Build</h2>
                            <p>From game studio websites to SaaS landing pages, we create web experiences that match your brand, audience, and business goals.</p>
                        </div>
                        <div className="business-web-type-grid">
                            {websiteTypes.map((item) => (
                                <article className="business-web-type-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <div>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="business-web-gallery-section space">
                    <div className="container">
                        <div className="business-web-split-heading">
                            <div className="business-web-heading mb-0">
                                <span>Page Sections</span>
                                <h2>Visual Website Sections for Modern Business Brands</h2>
                            </div>
                            <p>Use the right section system to build confidence: homepage hero, service details, product explanation, process, case studies, portfolio, and conversion blocks.</p>
                        </div>
                        <div className="business-web-gallery-grid">
                            {gallery.map((item) => (
                                <figure className="business-web-gallery-card" key={item.title}>
                                    <img src={item.image} alt={item.title} />
                                    <figcaption>{item.title}</figcaption>
                                </figure>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="business-web-process-section space">
                    <div className="container">
                        <div className="business-web-heading text-center">
                            <span>Website Process</span>
                            <h2>From Website Strategy to Launch</h2>
                        </div>
                        <div className="business-web-process-grid">
                            {process.map((item) => (
                                <article className="business-web-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="business-web-benefit-section space">
                    <div className="container">
                        <div className="business-web-heading text-center">
                            <span>Why Choose Oengines</span>
                            <h2>Websites Built for Search, Speed & Business Enquiries</h2>
                        </div>
                        <div className="business-web-benefit-grid">
                            {benefits.map((item) => (
                                <article className="business-web-benefit-card" key={item.title}>
                                    <span><i className={item.icon}></i></span>
                                    <div>
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="business-web-assets-section space">
                    <div className="container">
                        <div className="business-web-assets-panel">
                            <div className="business-web-heading mb-0">
                                <span>Deliverables</span>
                                <h2>Everything Needed for a Professional Business Website</h2>
                                <p>We prepare the page structure, design, development, SEO basics, forms, and launch checks needed for a business website.</p>
                            </div>
                            <div className="business-web-assets-list">
                                {deliverables.map((asset) => <span key={asset}>{asset}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <section className="business-web-faq-section space">
                    <div className="container">
                        <div className="business-web-heading text-center">
                            <span>FAQ</span>
                            <h2>Business Website Development Questions</h2>
                        </div>
                        <div className="business-web-faq-grid">
                            {faqs.map((item) => (
                                <article className="business-web-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="business-web-cta">
                    <div className="container">
                        <div className="business-web-cta-box">
                            <div>
                                <span>Website Consultation</span>
                                <h2>Build a business website that is clear, fast, and ready for leads.</h2>
                                <p>Share your company details, services, target audience, and website goals. We will plan a website structure that supports SEO and conversions.</p>
                            </div>
                            <a href="/contact" className="as-btn">Start Website Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
